import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BuddyLogo } from "../components/Brand";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";

const DOCS = {
  privacy: {
    title: "Privacy Policy",
    body: [
      "We collect only the minimum data needed to help students learn. We do not sell children's personal information and do not use it for targeted advertising.",
      "Student learning content, homework scans, and tutor conversations are stored securely and are viewable by the student, their linked parent/guardian, and (for school accounts) their assigned teacher.",
      "You can request deletion of your data at any time from the Settings screen.",
    ],
  },
  "childrens-privacy": {
    title: "Children's Privacy Notice",
    body: [
      "Children under the age of digital consent must be linked to a verified parent, guardian, teacher, or school account.",
      "We do not include public profiles, public chat rooms, direct messaging between unknown users, ads, or gambling features.",
      "Parents can review and delete their child's data at any time.",
    ],
  },
  terms: {
    title: "Terms of Service",
    body: [
      "AI Homework Buddy is an educational tool. It teaches students how to do homework — it does not do the homework for them.",
      "Do not misuse the service. Do not attempt to bypass academic integrity safeguards. Do not upload content that is not yours to share.",
    ],
  },
  "parent-consent": {
    title: "Parent Consent",
    body: [
      "By linking a child account to your parent account, you consent to your child using AI Homework Buddy under your supervision.",
      "You can revoke consent and delete the child's data at any time.",
    ],
  },
  "school-data": {
    title: "School Data Agreement",
    body: [
      "For School Edition accounts, school-provided student data is processed only for educational purposes.",
      "Schools may configure data retention, approved subjects, and AI settings from the School Admin portal.",
    ],
  },
  "ai-safety": {
    title: "AI Safety Policy",
    body: [
      "Lumi (our AI) uses age-appropriate explanations, refuses inappropriate content, does not encourage self-harm or violence, and never pretends to be a human doctor, attorney, therapist, or emergency service.",
      "Lumi will not ask students for addresses, phone numbers, private photos, passwords, or unnecessary personal information.",
      "Serious safety concerns are escalated to human review workflows.",
    ],
  },
  "academic-integrity": {
    title: "Academic Integrity Policy",
    body: [
      "We do not automatically generate complete essays or homework answers for submission.",
      "The writing coach guides students through brainstorming, outline, thesis, and revision — the writing is theirs.",
    ],
  },
  "accessibility-statement": {
    title: "Accessibility Statement",
    body: [
      "AI Homework Buddy is designed to be usable by students with a wide range of abilities: TalkBack labeling, Braille display support, dyslexia-friendly typography, high contrast, adjustable text, voice-first navigation, and reduced motion.",
      "We continue to improve accessibility with input from students, families, teachers, and specialists.",
    ],
  },
  support: {
    title: "Contact & Support",
    body: [
      "Need help? Reach out to support@aihomeworkbuddy.com and we'll respond within 1 business day.",
      "For schools and manufacturer partners, please contact partners@aihomeworkbuddy.com.",
    ],
  },
};

export default function Legal() {
  const { doc } = useParams();
  const { t, i18n } = useTranslation();
  const d = DOCS[doc] || DOCS.privacy;
  const isEnglish = i18n.resolvedLanguage === "en";
  return (
    <div className="min-h-screen adult-bg">
      <header className="hb-glass sticky top-0 z-30">
        <div className="max-w-[900px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/"><BuddyLogo /></Link>
          <div className="flex items-center gap-2"><LanguageSwitcher /><Link to="/"><Button variant="ghost">{t("common.home")}</Button></Link></div>
        </div>
      </header>
      <main className="max-w-[900px] mx-auto px-6 py-10">
        <h1 className="font-adult font-bold text-4xl text-slate-900">{d.title}</h1>
        {!isEnglish && (
          <div className="mt-4 p-3 rounded-xl bg-yellow-50 border border-yellow-200 text-sm text-slate-800">
            <b>ℹ️</b> This legal document is the authoritative English version. A certified translation is being prepared — please refer to the English text for legal interpretation.
          </div>
        )}
        <div className="mt-6 space-y-4 text-slate-700 leading-relaxed">
          {d.body.map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
        </div>
        <p className="text-xs text-slate-500 mt-8">
          This document is a plain-English overview and does not constitute legal advice. Contact qualified professionals for compliance review.
        </p>
      </main>
    </div>
  );
}
